import { Button } from '@zillow/constellation-10';
import { IconMailFilled, IconSparkleFilled } from '@zillow/constellation-icons';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import useInboxRouterHelper from '../../../hooks/useInboxRouterHelper';
import StringConstants from '../../../constants/StringConstants';
import EmptyState from '../../EmptyState';

const NO_CONVERSATIONS_TEXT = 'This folder is empty.';
const NO_CONVERSATIONS_DEFAULT = 'When a renter messages you about a property, it will show up here.';
const HAS_MORE_THAN_5_LISTINGS_OVER_90_DAYS_CONVERSATION =
  'To get older conversations, try selecting a single property.';
const OVERDUE_MESSAGES_EMPTY_TEXT = "You're all caught up. Well done!";
const FOLDER_TEXT_OPTIONS = {
  [SatelliteConversationFolderTypeV2.ARCHIVED]: 'You can move old messages out of your inbox and into your archive.',
  [SatelliteConversationFolderTypeV2.SPAM]: 'Clear reported messages from your inbox.',
  [SatelliteConversationFolderTypeV2.FAVORITE]: 'You have no conversations marked as starred.',
  // Need ALL Folder options (that are reachable) in order to use enum value as a key
  [SatelliteConversationFolderTypeV2.SHOW_ALL]: '',
};

type Props = {
  folder: SatelliteConversationFolderTypeV2;
  hasConversationSearchText: boolean;
  hasMoreThan5Listings: boolean;
  isByProperty: boolean;
  readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2;
  renterProgressStatus: SatelliteConversationRenterProgressStatusTypeV2;
  showErrorMessage: boolean;
};

export default function EmptyContainer({
  folder,
  hasConversationSearchText,
  hasMoreThan5Listings,
  isByProperty,
  readRepliedStatus,
  renterProgressStatus,
  showErrorMessage,
}: Props): JSX.Element {
  const { resetFiltersAndFolders } = useInboxRouterHelper();
  let messageCopy: string;
  const hasNoFilters =
    folder === SatelliteConversationFolderTypeV2.INBOX &&
    readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL &&
    renterProgressStatus === SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL &&
    !isByProperty &&
    !hasConversationSearchText;
  const ctaText = hasConversationSearchText ? 'Clear search' : 'Reset filters';

  if (showErrorMessage) {
    messageCopy = StringConstants.GENERIC_ERROR;
  } else if (readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.NOT_REPLIED_TO) {
    messageCopy = OVERDUE_MESSAGES_EMPTY_TEXT;
  } else if (hasMoreThan5Listings && hasNoFilters) {
    messageCopy = HAS_MORE_THAN_5_LISTINGS_OVER_90_DAYS_CONVERSATION;
  } else if (hasNoFilters) {
    messageCopy = NO_CONVERSATIONS_DEFAULT;
  } else {
    messageCopy =
      folder !== SatelliteConversationFolderTypeV2.INBOX ? FOLDER_TEXT_OPTIONS[folder] : NO_CONVERSATIONS_TEXT;
  }

  return (
    <EmptyState
      icon={
        readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.NOT_REPLIED_TO ? (
          <IconSparkleFilled />
        ) : (
          <IconMailFilled />
        )
      }
      body={messageCopy}
      css={{ height: '100%', justifyContent: 'center', paddingBottom: 'tighter', paddingInline: 'default' }}
      cta={
        !hasNoFilters ? (
          <Button emphasis="outlined" onClick={resetFiltersAndFolders} size="sm" tone="brand">
            {ctaText}
          </Button>
        ) : undefined
      }
    />
  );
}
